import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PublicHeader from "../components/PublicHeader";
import Footer from "../components/Footer";
import HeroGraphic from "../components/home/HeroGraphic";
import DemoVideo from "../components/home/DemoVideo";
import {
  WorksWith, Benefits, HowItWorks, WhoFor, Pricing, Trust, FinalCta,
} from "../components/home/HomeSections";

const TITLE = "Lingua Franca | Vendor invoices delivered into your accounting system";
const SUBHEAD = "Lingua Franca delivers your vendors' invoices straight into your accounting system as bills — every line, amount and tax intact. No re-keying, no PDFs, no inbox digging.";
const HERO_SUBHEAD = "When your vendors are on Lingua Franca, their invoices land in your accounting system as bills with every line intact. You review, approve, and pay as usual.";

function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function Hero() {
  const scrollToHow = () => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" });
  return (
    <section className="relative overflow-hidden" data-testid="home-hero">
      <div className="absolute inset-0 grid-overlay opacity-60 pointer-events-none" aria-hidden="true" />
      <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-20 lg:pt-24 lg:pb-28 grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-14 lg:gap-10 items-center">
        <div>
          <div className="label-mini lf-fade-up">invoice clearinghouse</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02] mt-4 lf-fade-up"
              style={{ animationDelay: "80ms" }} data-testid="home-hero-title">
            Stop re-typing your vendors’ invoices.
          </h1>
          <p className="text-sm sm:text-base text-neutral-600 leading-relaxed mt-6 max-w-lg lf-fade-up"
             style={{ animationDelay: "160ms" }} data-testid="home-hero-subhead">
            {HERO_SUBHEAD}
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8 lf-fade-up" style={{ animationDelay: "240ms" }}>
            <Link to="/register" className="btn-primary inline-flex items-center gap-2 px-6 py-3 transition-transform duration-200 hover:-translate-y-0.5"
                  data-testid="hero-get-started">
              Get started free <ArrowRight size={14} />
            </Link>
            <button type="button" onClick={scrollToHow} className="btn-outline px-6 py-3"
                    data-testid="hero-see-how">
              See how it works
            </button>
          </div>
          <p className="text-xs text-neutral-500 mt-4 lf-fade-up" style={{ animationDelay: "300ms" }}
             data-testid="hero-free-line">
            Free for 6 months or your first 100 invoices.
          </p>
        </div>
        <div className="lf-fade-up" style={{ animationDelay: "380ms" }}>
          <HeroGraphic />
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  useEffect(() => {
    document.title = TITLE;
    setMeta("name", "description", SUBHEAD);
    setMeta("property", "og:title", TITLE);
    setMeta("property", "og:description", SUBHEAD);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white overflow-x-hidden" data-testid="home-page">
      <PublicHeader compactOnMobile />
      <main className="flex-1">
        <Hero />
        <DemoVideo />
        <WorksWith />
        <Benefits />
        <HowItWorks />
        <WhoFor />
        <Pricing />
        <Trust />
        <FinalCta />
      </main>
      <Footer />
    </div>
  );
}
